// Reading a whole result set from PostgREST.
//
// The live project caps every response at 1,000 rows (`max_rows`) and does it
// SILENTLY: `.limit(5000)` or no limit at all just returns the first 1,000 with
// no error, so a list built from one query quietly drops everything past it.
// `pageAll` asks for one page at a time with `.range()` until a page comes back
// short, so a list is either complete or throws.
//
// No React / Supabase in this file so it is unit-tested directly.

export interface PageResult<T> {
  data: T[] | null;
  error: unknown;
}

export interface PageAllOptions {
  /** Rows asked for per request. Must not exceed the server's `max_rows`
   *  (1,000), or a full page would look like a short one and end the loop. */
  pageSize?: number;
  /** Stop once at least this many rows are in. A safety net, not a target. */
  maxRows?: number;
}

/**
 * `fetchRange(from, to)` is inclusive on both ends, like PostgREST's `.range()`.
 * Rejects with the server's error rather than returning a partial list.
 */
export async function pageAll<T>(
  fetchRange: (from: number, to: number) => PromiseLike<PageResult<T>>,
  opts: PageAllOptions = {},
): Promise<T[]> {
  const pageSize = opts.pageSize ?? 1000;
  const maxRows = opts.maxRows ?? 20_000;
  const out: T[] = [];
  for (let from = 0; out.length < maxRows; from += pageSize) {
    const { data, error } = await fetchRange(from, from + pageSize - 1);
    if (error) throw error;
    const rows = data ?? [];
    out.push(...rows);
    if (rows.length < pageSize) break;
  }
  return out;
}

/** Splits a list into pieces of at most `size` (for `.in('id', ids)` calls, whose
 *  ids all travel in the URL). */
export function chunk<T>(items: readonly T[], size: number): T[][] {
  if (!(size >= 1)) throw new Error('chunk: size must be at least 1');
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}
